import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Shield, Zap, Globe, Sparkles } from "lucide-react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import GlobalMetaforaLogo from "../GlobalMetaforaLogo";
import GlowingOrb from "../effects/GlowingOrb";
import { MagneticButton, RevealOnScroll } from "../effects/AnimatedElements";
import { useLanguage } from "@/context/LanguageContext";

const defaultContent = {
  badge_text: "+20 Años de Experiencia",
  title_line1: "Sistemas Operativos",
  title_line2: "Integrales",
  title_line3: "para el Futuro Empresarial",
  subtitle: "Desarrollamos soluciones tecnológicas personalizadas que transforman la manera en que las empresas operan. Seguridad, velocidad y control total.",
  cta_primary: "Ver Planes",
  cta_secondary: "Solicitar Demo",
  background_image: "https://images.unsplash.com/photo-1771013304380-dfaf9f928de6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwY29ubmVjdGlvbiUyMGJsdWUlMjBkb3RzJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzczMjcwODExfDA&ixlib=rb-4.1.0&q=85"
};

const HeroSection = ({ content, onSelectPlan }) => {
  const data = { ...defaultContent, ...content };
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);

  const scrollToServices = () => {
    document.querySelector("#servicios")?.scrollIntoView({ behavior: "smooth" });
  };

  // Text animation variants
  const titleVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.3,
      },
    },
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 50, rotateX: -90 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
      },
    },
  };

  const splitText = (text) => {
    return text.split("").map((char, index) => (
      <motion.span
        key={index}
        variants={letterVariants}
        className="inline-block"
        style={{ display: char === " " ? "inline" : "inline-block" }}
      >
        {char === " " ? "\u00A0" : char}
      </motion.span>
    ));
  };

  return (
    <section
      ref={containerRef}
      data-testid="hero-section"
      className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Animated Background */}
      <motion.div
        className="hero-bg absolute inset-0"
        style={{ y, scale }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${data.background_image}')` }}
        />
      </motion.div>
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#02040a]/50 via-[#02040a]/70 to-[#02040a]" />
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-[#02040a]" />
      
      {/* Animated Orbs */}
      <GlowingOrb color="#3b82f6" size={600} className="top-0 -left-40" />
      <GlowingOrb color="#06b6d4" size={500} className="bottom-0 -right-40" />
      <GlowingOrb color="#8b5cf6" size={400} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      {/* Floating geometric shapes */}
      <motion.div
        className="absolute top-20 left-20 w-20 h-20 border border-[#3b82f6]/30 rotate-45"
        animate={{ rotate: [45, 225, 45], scale: [1, 1.1, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute bottom-40 right-20 w-16 h-16 border border-[#06b6d4]/30"
        animate={{ rotate: [0, 360], y: [0, -20, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute top-1/3 right-1/4 w-3 h-3 bg-[#3b82f6] rounded-full"
        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/3 left-1/4 w-2 h-2 bg-[#06b6d4] rounded-full"
        animate={{ scale: [1, 2, 1], opacity: [0.3, 0.8, 0.3] }}
        transition={{ duration: 4, repeat: Infinity, delay: 1 }}
      />

      {/* Content */}
      <motion.div 
        className="relative z-10 section-container text-center px-4"
        style={{ opacity }}
      >
        {/* Animated Logo */}
        <motion.div
          initial={{ scale: 0, rotate: -180, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ 
            type: "spring", 
            duration: 1.5, 
            bounce: 0.4,
            delay: 0.2 
          }}
          className="flex justify-center mb-8"
        >
          <motion.div
            animate={{ 
              y: [0, -10, 0],
              rotateY: [0, 5, 0, -5, 0]
            }}
            transition={{ 
              duration: 6, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
          >
            <GlobalMetaforaLogo size={160} />
          </motion.div>
        </motion.div>

        {/* Brand Name - Prominent */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mb-4"
        >
          <span className="text-[#3b82f6]/60 text-xs sm:text-sm font-bold tracking-[0.4em] uppercase">
            {t("hero_powered")}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 1, type: "spring" }}
          className="mb-6"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
            <span className="text-white">GLOBAL </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3b82f6] to-[#06b6d4]">METAFORA</span>
            <span className="text-white"> APP</span>
          </h2>
        </motion.div>

        {/* Animated Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-[#3b82f6]/10 border border-[#3b82f6]/30 px-4 py-2 mb-8 backdrop-blur-sm"
        >
          <motion.span 
            className="w-2 h-2 rounded-full bg-[#3b82f6]"
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <Sparkles className="w-4 h-4 text-[#3b82f6]" />
          <span className="text-[#3b82f6] text-sm font-medium tracking-wider uppercase">
            {t("hero_experience")}
          </span>
        </motion.div>

        {/* Animated Title */}
        <motion.h1 
          data-testid="hero-title"
          className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          variants={titleVariants}
          initial="hidden"
          animate="visible"
        >
          <span className="block">{splitText(t("hero_title_1"))}</span>
          <motion.span 
            className="block gradient-text"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.8, type: "spring" }}
          >
            {t("hero_title_2")}
          </motion.span>
          <span className="block">{splitText(t("hero_title_3"))}</span>
        </motion.h1>

        {/* Subtitle with typewriter effect */}
        <motion.p 
          data-testid="hero-subtitle"
          className="text-[#94a3b8] text-lg sm:text-xl max-w-2xl mx-auto mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          {t("hero_subtitle")}
        </motion.p>

        {/* Animated CTA Buttons */}
        <motion.div 
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.5 }}
        >
          <MagneticButton>
            <motion.button
              data-testid="hero-cta-plans"
              onClick={() => document.querySelector("#precios")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-primary text-lg group relative overflow-hidden"
              whileHover={{ boxShadow: "0 0 40px rgba(59, 130, 246, 0.6)" }}
            >
              <motion.span
                className="absolute inset-0 bg-white/20"
                initial={{ x: "-100%", skewX: -15 }}
                whileHover={{ x: "100%" }}
                transition={{ duration: 0.5 }}
              />
              <span className="relative">{t("hero_cta_plans")}</span>
            </motion.button>
          </MagneticButton>
          
          <MagneticButton>
            <motion.button
              data-testid="hero-cta-demo"
              onClick={() => navigate("/demo")}
              className="btn-secondary text-lg relative overflow-hidden group"
              whileHover={{ borderColor: "#3b82f6" }}
            >
              <motion.span
                className="absolute inset-0 bg-[#3b82f6]/10"
                initial={{ scale: 0, opacity: 0 }}
                whileHover={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{ borderRadius: "inherit" }}
              />
              <span className="relative">{t("hero_cta_demo")}</span>
            </motion.button>
          </MagneticButton>
        </motion.div>

        {/* Animated Features Pills */}
        <motion.div 
          className="flex flex-wrap items-center justify-center gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.5 }}
        >
          {[
            { icon: Shield, text: t("hero_pill_1"), color: "#3b82f6" },
            { icon: Zap, text: t("hero_pill_2"), color: "#06b6d4" },
            { icon: Globe, text: t("hero_pill_3"), color: "#f43f5e" },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-2 text-[#94a3b8] px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.2 + index * 0.1 }}
              whileHover={{ 
                scale: 1.05, 
                borderColor: item.color,
                boxShadow: `0 0 20px ${item.color}30`
              }}
            >
              <item.icon className="w-5 h-5" style={{ color: item.color }} />
              <span className="text-sm">{item.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Animated Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.button
          data-testid="scroll-down"
          onClick={scrollToServices}
          className="text-[#94a3b8] hover:text-white transition-colors flex flex-col items-center gap-2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-xs uppercase tracking-widest">{t("hero_explore")}</span>
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown size={32} />
          </motion.div>
        </motion.button>
      </motion.div>

      {/* Background watermark - Brand name */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none z-[1]">
        <div className="absolute bottom-32 left-0 right-0 opacity-[0.03]">
          <div className="whitespace-nowrap text-[140px] sm:text-[200px] font-black text-white tracking-wider text-center">
            GLOBAL METAFORA APP
          </div>
        </div>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-[#3b82f6]/20" />
      <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-[#3b82f6]/20" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-[#3b82f6]/20" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-[#3b82f6]/20" />
    </section>
  );
};

export default HeroSection;
